import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import c from 'classnames'
import CloseIcon from 'mdi-react/CloseIcon'
import ChevronDownIcon from 'mdi-react/ChevronDownIcon'
import MenuIcon from 'mdi-react/MenuIcon'
import PublicAppLogo from 'presentation/PublicAppLogo'
import { CustomerSelfManagementStoreContext } from 'providers/PublicStoreProvider'
import { CUSTOMER_SELF_MANAGEMENT_STEPS } from 'stores/CustomerSelfManagement/CustomerSelfManagementStore'
import { withIdentityNumberQuery, normalizePlate } from 'util/customerSelfManagementSession'
import {
    CUSTOMER_SELF_MANAGEMENT,
    CUSTOMER_SELF_MANAGEMENT_ACCIDENTS,
    CUSTOMER_SELF_MANAGEMENT_CHANGE_PAYMENT_METHOD,
    CUSTOMER_SELF_MANAGEMENT_CLIENTS,
    CUSTOMER_SELF_MANAGEMENT_DOCUMENTS,
    CUSTOMER_SELF_MANAGEMENT_MODIFY_POLICY_DATA,
    CUSTOMER_SELF_MANAGEMENT_PAYMENT_REJECTIONS,
} from 'routing/routes'

import styles from './publicHeader.module.scss'

const COMPACT_SCROLL_THRESHOLD = 16
const MOBILE_HEADER_MAX_WIDTH = '(max-width: 920px)'
const SESSION_PANEL_ANIMATION_MS = 220

const NAV_ITEMS = [
    {
        id: 'documentos',
        label: 'Documentos',
        match: CUSTOMER_SELF_MANAGEMENT_DOCUMENTS,
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.DOCUMENTS,
        getPath: (base) => `${base}${CUSTOMER_SELF_MANAGEMENT_DOCUMENTS}`,
    },
    {
        id: 'inspecciones',
        label: 'Inspecciones',
        match: '/inspecciones',
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.INSPECTIONS,
        getPath: (base, inspectionId) => (
            inspectionId
                ? `${base}/inspecciones/${inspectionId}`
                : `${base}${CUSTOMER_SELF_MANAGEMENT_CLIENTS}`
        ),
    },
    {
        id: 'siniestros',
        label: 'Siniestros',
        match: CUSTOMER_SELF_MANAGEMENT_ACCIDENTS,
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.ACCIDENT,
        getPath: (base) => `${base}${CUSTOMER_SELF_MANAGEMENT_ACCIDENTS}`,
    },
    {
        id: 'rechazos',
        label: 'Rechazos de pagos',
        match: CUSTOMER_SELF_MANAGEMENT_PAYMENT_REJECTIONS,
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.PAYMENT_REJECTION,
        getPath: (base) => `${base}${CUSTOMER_SELF_MANAGEMENT_PAYMENT_REJECTIONS}`,
    },
    {
        id: 'pagos',
        label: 'Cambiar forma de pago',
        match: CUSTOMER_SELF_MANAGEMENT_CHANGE_PAYMENT_METHOD,
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.CHANGE_PAYMENT_METHOD,
        getPath: (base) => `${base}${CUSTOMER_SELF_MANAGEMENT_CHANGE_PAYMENT_METHOD}`,
    },
    {
        id: 'datos',
        label: 'Modificar datos de póliza',
        match: CUSTOMER_SELF_MANAGEMENT_MODIFY_POLICY_DATA,
        step: CUSTOMER_SELF_MANAGEMENT_STEPS.MODIFY_POLICY_DATA,
        getPath: (base) => `${base}${CUSTOMER_SELF_MANAGEMENT_MODIFY_POLICY_DATA}`,
    },
]

const PublicHeader = ({ platform, goBack, logo }) => {
    const { t } = useTranslation('customerSelfManagement')
    const history = useHistory()
    const location = useLocation()
    const customerSelfManagementStore = useContext(CustomerSelfManagementStoreContext)
    const {
        colors,
        inspection,
        sessionIdentityNumber,
        sessionPlate,
        sessionPolicyNumber,
        sessionVehicleLabel,
        sessionVehicleKind,
    } = customerSelfManagementStore
    const [isCompact, setIsCompact] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isSessionOpen, setIsSessionOpen] = useState(false)
    const [sessionPanelPhase, setSessionPanelPhase] = useState(null)
    const sessionCloseTimerRef = useRef(null)
    const sessionOpenRef = useRef(false)
    const prevPathnameRef = useRef(null)

    useEffect(() => {
        sessionOpenRef.current = isSessionOpen
    }, [isSessionOpen])

    const openSessionPanel = useCallback(() => {
        if (sessionCloseTimerRef.current) {
            window.clearTimeout(sessionCloseTimerRef.current)
            sessionCloseTimerRef.current = null
        }

        setIsSessionOpen(true)
        setSessionPanelPhase('in')
    }, [])

    const closeSessionPanel = useCallback(() => {
        if (!sessionOpenRef.current || sessionCloseTimerRef.current) {
            return
        }

        setSessionPanelPhase('out')

        sessionCloseTimerRef.current = window.setTimeout(() => {
            setIsSessionOpen(false)
            setSessionPanelPhase(null)
            sessionCloseTimerRef.current = null
        }, SESSION_PANEL_ANIMATION_MS)
    }, [])

    const toggleSessionPanel = useCallback(() => {
        if (isSessionOpen) {
            closeSessionPanel()
        } else {
            openSessionPanel()
        }
    }, [closeSessionPanel, isSessionOpen, openSessionPanel])
    const platformLink = platform ? `${CUSTOMER_SELF_MANAGEMENT}/${platform}` : `${CUSTOMER_SELF_MANAGEMENT}/pasap`
    const sessionIdentity = sessionIdentityNumber || null
    const isInnerOption = Boolean(
        platform
        && location.pathname.startsWith(`${CUSTOMER_SELF_MANAGEMENT}/${platform}/`)
    )

    useEffect(() => {
        let ticking = false
        const mobileQuery = window.matchMedia(MOBILE_HEADER_MAX_WIDTH)

        const update = () => {
            setIsMobile(mobileQuery.matches)

            if (mobileQuery.matches) {
                setIsCompact(window.scrollY > COMPACT_SCROLL_THRESHOLD)
            } else {
                setIsCompact(false)
                setIsMenuOpen(false)
            }
        }

        const onScroll = () => {
            if (ticking) {
                return
            }

            ticking = true
            window.requestAnimationFrame(() => {
                ticking = false
                update()
            })
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        mobileQuery.addEventListener('change', update)

        return () => {
            window.removeEventListener('scroll', onScroll)
            mobileQuery.removeEventListener('change', update)
        }
    }, [])

    useEffect(() => {
        setIsMenuOpen(false)
        closeSessionPanel()
    }, [closeSessionPanel, location.pathname])

    useEffect(() => () => {
        if (sessionCloseTimerRef.current) {
            window.clearTimeout(sessionCloseTimerRef.current)
        }
    }, [])

    useEffect(() => {
        const { pathname, search } = history.location
        const params = new URLSearchParams(search)
        const identityFromUrl = params.get('identityNumber')
        const plateFromUrl = params.get('plate')
        const policyNumberFromUrl = params.get('policyNumber')
        const plateValue = normalizePlate(sessionPlate)
        const policyNumberValue = sessionPolicyNumber ? String(sessionPolicyNumber).trim() : null
        const urlPlate = normalizePlate(plateFromUrl)
        const urlPolicyNumber = policyNumberFromUrl?.trim() || null
        const urlHasPlate = Boolean(urlPlate)
        const urlHasPolicyNumber = Boolean(urlPolicyNumber)
        const pathnameChanged = prevPathnameRef.current !== pathname
        const isFirstLoad = prevPathnameRef.current === null

        prevPathnameRef.current = pathname

        // Deep link / navegación: la URL manda. No aplicar en cada keystroke (evita pisar lo tipeado).
        if ((isFirstLoad || pathnameChanged) && (urlHasPlate || urlHasPolicyNumber)) {
            if (identityFromUrl && identityFromUrl !== customerSelfManagementStore.identityNumber?.value) {
                customerSelfManagementStore.setIdentityNumber(identityFromUrl)
            }

            customerSelfManagementStore.setPlate(urlHasPlate ? urlPlate : null)
            customerSelfManagementStore.setPolicyNumberInput(urlHasPolicyNumber ? urlPolicyNumber : null)
            // Patente/póliza en inputs (desde URL) pero en cookie solo DNI hasta validar policy-preview.
            customerSelfManagementStore.persistIdentityLightSession()
            return
        }

        if (identityFromUrl && identityFromUrl !== customerSelfManagementStore.sessionIdentityNumber) {
            customerSelfManagementStore.setIdentityNumber(identityFromUrl)
            customerSelfManagementStore.persistIdentityLightSession()
        }

        if (!isInnerOption || !sessionIdentity) {
            return
        }

        let shouldReplace = false

        if (identityFromUrl !== sessionIdentity) {
            params.set('identityNumber', sessionIdentity)
            shouldReplace = true
        }

        if (plateValue) {
            if (plateFromUrl !== plateValue) {
                params.set('plate', plateValue)
                shouldReplace = true
            }
        } else if (plateFromUrl) {
            params.delete('plate')
            shouldReplace = true
        }

        if (policyNumberValue) {
            if (policyNumberFromUrl !== policyNumberValue) {
                params.set('policyNumber', policyNumberValue)
                shouldReplace = true
            }
        } else if (policyNumberFromUrl) {
            params.delete('policyNumber')
            shouldReplace = true
        }

        // Evitar plate + policyNumber juntos en la URL.
        if (plateValue && params.has('policyNumber')) {
            params.delete('policyNumber')
            shouldReplace = true
        }

        if (!plateValue && policyNumberValue && params.has('plate')) {
            params.delete('plate')
            shouldReplace = true
        }

        if (shouldReplace) {
            const query = params.toString()
            // Usar pathname actual del history (no el de un render viejo) para no pisar un redirect recién hecho.
            history.replace(`${history.location.pathname}${query ? `?${query}` : ''}`)
        }
    }, [
        customerSelfManagementStore,
        history,
        isInnerOption,
        location.pathname,
        location.search,
        sessionIdentity,
        sessionPlate,
        sessionPolicyNumber,
    ])

    const sessionQuery = (path) => withIdentityNumberQuery(
        path,
        sessionIdentity,
        sessionPlate,
        sessionPolicyNumber,
    )

    const handleNavClick = (item) => (event) => {
        event.preventDefault()
        setIsMenuOpen(false)
        customerSelfManagementStore.setStep(item.step)
        history.push(sessionQuery(item.getPath(platformLink, inspection?.id)))
    }

    const handleCloseSession = (event) => {
        event.preventDefault()
        event.stopPropagation()
        setIsMenuOpen(false)
        closeSessionPanel()
        customerSelfManagementStore.closeSession()
        history.replace(platformLink)
    }

    const handleChangeVehicle = (event) => {
        event.preventDefault()
        event.stopPropagation()
        setIsMenuOpen(false)
        closeSessionPanel()
        customerSelfManagementStore.changeVehicleSession(history)
    }

    const renderNavLinks = (inMenu = false) => (
        NAV_ITEMS.map((item) => {
            const to = sessionQuery(item.getPath(platformLink, inspection?.id))
            const isActive = location.pathname.includes(item.match)

            return (
                <Link
                    key={item.id}
                    to={to}
                    className={c(
                        inMenu ? styles.menuLink : styles.navLink,
                        isActive && (inMenu ? styles.menuLinkActive : styles.navLinkActive)
                    )}
                    onClick={handleNavClick(item)}
                >
                    {item.label}
                </Link>
            )
        })
    )

    const sessionTriggerLabel = sessionVehicleLabel
        ? `${sessionIdentity} · ${sessionVehicleLabel}`
        : sessionIdentity

    return (
        <div
            className={c(
                styles.publicHeaderContainer,
                isCompact && styles.compact,
                isInnerOption && styles.withNav,
                sessionIdentity && styles.withSession
            )}
            style={{ '--primary-color': colors?.primary }}
        >
            <div className={styles.sideSlot}>
                <Link to={platformLink} className={styles.publicAppLogoContainer} onClick={goBack}>
                    <PublicAppLogo logo={logo} className={styles.logo} />
                </Link>
            </div>

            {isInnerOption && !isMobile ? (
                <nav className={styles.nav} aria-label="Autogestión">
                    {renderNavLinks()}
                </nav>
            ) : (
                <div className={styles.navSpacer} />
            )}

            <div className={c(styles.sideSlot, styles.sideSlotEnd)}>
                {sessionIdentity && (
                    <div className={styles.sessionMenu}>
                        <button
                            type="button"
                            className={c(styles.sessionTrigger, isSessionOpen && styles.sessionTriggerOpen)}
                            onClick={toggleSessionPanel}
                            aria-expanded={isSessionOpen}
                            aria-haspopup="true"
                            title={sessionTriggerLabel}
                        >
                            <span className={styles.sessionTriggerText}>
                                <span className={styles.sessionTriggerLabel}>{t('sessionMenu')}</span>
                                <span className={styles.sessionTriggerValue}>{sessionTriggerLabel}</span>
                            </span>
                            <ChevronDownIcon
                                size={16}
                                className={c(styles.sessionChevron, isSessionOpen && styles.sessionChevronOpen)}
                            />
                        </button>

                        {isSessionOpen && (
                            <>
                                <button
                                    type="button"
                                    className={c(
                                        styles.sessionBackdrop,
                                        sessionPanelPhase === 'in' && styles.sessionBackdropIn,
                                        sessionPanelPhase === 'out' && styles.sessionBackdropOut,
                                    )}
                                    aria-label={t('sessionMenu')}
                                    onClick={closeSessionPanel}
                                />
                                <div
                                    className={c(
                                        styles.sessionPanel,
                                        sessionPanelPhase === 'in' && styles.sessionPanelIn,
                                        sessionPanelPhase === 'out' && styles.sessionPanelOut,
                                    )}
                                    role="menu"
                                >
                                    <div className={styles.sessionPanelRow}>
                                        <span className={styles.sessionPanelLabel}>{t('sessionDni')}</span>
                                        <span className={styles.sessionPanelValue}>{sessionIdentity}</span>
                                        <button
                                            type="button"
                                            className={styles.sessionPanelAction}
                                            onClick={handleCloseSession}
                                            aria-label={t('closeSession')}
                                        >
                                            {t('closeSession')}
                                        </button>
                                    </div>
                                    {sessionVehicleLabel && (
                                        <div className={styles.sessionPanelRow}>
                                            <span className={styles.sessionPanelLabel}>
                                                {sessionVehicleKind === 'policy' ? t('sessionPolicy') : t('plate')}
                                            </span>
                                            <span className={styles.sessionPanelValue}>{sessionVehicleLabel}</span>
                                            <button
                                                type="button"
                                                className={styles.sessionPanelAction}
                                                onClick={handleChangeVehicle}
                                            >
                                                {t('changeVehicle')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
                {isInnerOption && isMobile && (
                    <button
                        type="button"
                        className={styles.menuButton}
                        onClick={() => setIsMenuOpen((open) => !open)}
                        aria-label={t('menu')}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <CloseIcon size={22} /> : <MenuIcon size={22} />}
                    </button>
                )}
            </div>

            {isInnerOption && isMobile && isMenuOpen && (
                <>
                    <button
                        type="button"
                        className={styles.menuBackdrop}
                        aria-label={t('menu')}
                        onClick={() => setIsMenuOpen(false)}
                    />
                    <nav className={styles.mobileMenu} aria-label="Autogestión">
                        {renderNavLinks(true)}
                    </nav>
                </>
            )}
        </div>
    )
}

PublicHeader.propTypes = {
    platform: PropTypes.string,
    goBack: PropTypes.func.isRequired,
    logo: PropTypes.string
}

PublicHeader.defaultProps = {
    platform: null,
    logo: null,
}

export default observer(PublicHeader)
